/**
 * Administration → Access → Two-step sign-in (1.0.0-rc.4): the feature for the installation, who must use it (everyone,
 * or chosen roles and access groups), and whether people signed in through single sign-on are asked for PRISM's
 * code too. Each member's state and the reset are in the member list. 1.0.0-rc.5: how many days a browser ticked at
 * the code step skips the code on the same network ("Remember devices for N days"; 0 turns it off).
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldCheck } from 'lucide-react';
import { api, errorMessage } from '../../api/client';
import { useAccessGroups, useRoles } from '../../api/hooks';
import { toast } from '../../components/Toasts';
import { queryKeys } from '../../api/queryKeys';

interface Policy { enabled: boolean; requireAll: boolean; roles: string[]; groups: string[]; ssoTotp: boolean; rememberDays: number; enrolled: number; members: number }

/** The longest an administrator can choose (the API's MAX_REMEMBER_DAYS). */
const MAX_REMEMBER_DAYS = 90;

export function MfaPolicyAdmin() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['mfa-policy'], queryFn: () => api<Policy>('/api/v1/system/mfa-policy') });
  const roles = useRoles().data?.items ?? [];
  const groups = useAccessGroups().data?.items ?? [];
  const [p, setP] = useState<Policy | null>(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<'nobody' | 'everyone' | 'chosen' | null>(null);
  useEffect(() => {
    if (q.data && !p) {
      setP(q.data);
      setMode(q.data.requireAll ? 'everyone' : q.data.roles.length || q.data.groups.length ? 'chosen' : 'nobody');
    }
  }, [q.data, p]);
  if (!p) return <section className="panel-card" style={{ marginBottom: 20 }}><div className="skeleton" style={{ height: 120 }} /></section>;
  const who = mode ?? 'nobody';
  const dirty = JSON.stringify({ ...p, enrolled: 0, members: 0 }) !== JSON.stringify({ ...q.data, enrolled: 0, members: 0 });
  const toggle = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  async function save() {
    if (!p) return;
    if (p.enabled && (p.requireAll || p.roles.includes('admin')) && !window.confirm('Administrators will need two-step sign-in too. If you have not set it up yourself, PRISM asks you to do so on your next request. Continue?')) return;
    setBusy(true);
    try {
      const saved = await api<Policy>('/api/v1/system/mfa-policy', { method: 'PUT', json: { enabled: p.enabled, requireAll: p.requireAll, roles: p.roles, groups: p.groups, ssoTotp: p.ssoTotp, rememberDays: p.rememberDays } });
      setP({ ...p, ...saved });
      toast('Two-step sign-in policy saved');
      await qc.invalidateQueries({ queryKey: ['mfa-policy'] });
      await qc.invalidateQueries({ queryKey: queryKeys.me() });
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="panel-card" style={{ marginBottom: 20 }} data-testid="mfa-policy">
      <h3><ShieldCheck size={16} color="var(--accent-strong)" /> Two-step sign-in</h3>
      <p className="muted" style={{ margin: 0 }}>A code from an authenticator app (TOTP) after the password. People set it up under My account and get ten one-time recovery codes.
        {' '}{q.data ? `${q.data.enrolled} of ${q.data.members} members have set it up.` : ''} API keys, storage agents, share links and the PRISM Graphics editor are not affected.</p>
      <label className="toggle-row"><input type="checkbox" checked={p.enabled} onChange={(e) => setP({ ...p, enabled: e.target.checked })} data-testid="mfa-enabled" />
        <span><b>Turn on two-step sign-in for this installation</b><br /><span className="faint" style={{ fontSize: 12 }}>Off: nobody is asked for a code, and set-up authenticators wait unused.</span></span></label>
      {p.enabled && (
        <>
          <div className="field">
            <label>Required for</label>
            <div className="radio-row">
              <label><input type="radio" name="mfa-who" checked={who === 'nobody'} onChange={() => { setMode('nobody'); setP({ ...p, requireAll: false, roles: [], groups: [] }); }} /> Nobody (optional for everyone)</label>
              <label><input type="radio" name="mfa-who" checked={who === 'everyone'} onChange={() => { setMode('everyone'); setP({ ...p, requireAll: true, roles: [], groups: [] }); }} data-testid="mfa-require-all" /> Everyone</label>
              <label><input type="radio" name="mfa-who" checked={who === 'chosen'} onChange={() => { setMode('chosen'); setP({ ...p, requireAll: false }); }} data-testid="mfa-require-chosen" /> Chosen roles and access groups</label>
            </div>
          </div>
          {who === 'chosen' && (
            <div className="field-row">
              <div className="field"><label>Roles</label>
                <div className="check-list" data-testid="mfa-roles">{roles.map((r) => (
                  <label key={r.key}><input type="checkbox" checked={p.roles.includes(r.key)} onChange={() => setP({ ...p, roles: toggle(p.roles, r.key) })} /> {r.name}</label>
                ))}</div></div>
              <div className="field"><label>Access groups</label>
                <div className="check-list" data-testid="mfa-groups">{groups.length ? groups.map((g) => (
                  <label key={g.id}><input type="checkbox" checked={p.groups.includes(g.id)} onChange={() => setP({ ...p, groups: toggle(p.groups, g.id) })} /> {g.name}</label>
                )) : <span className="faint">No access groups yet</span>}</div></div>
            </div>
          )}
          <p className="faint" style={{ margin: 0, fontSize: 12 }}>Someone required to use it who has not set it up is taken to set it up at their next sign-in, before anything else. They cannot turn it off while it is required.</p>
          <label className="toggle-row"><input type="checkbox" checked={p.ssoTotp} onChange={(e) => setP({ ...p, ssoTotp: e.target.checked })} data-testid="mfa-sso-totp" />
            <span><b>Also ask people who sign in with single sign-on</b><br /><span className="faint" style={{ fontSize: 12 }}>Off (the default): their identity provider’s own MFA covers them.</span></span></label>
          <div className="field" data-testid="mfa-remember">
            <label htmlFor="mfa-remember-days">Remember devices for</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input id="mfa-remember-days" className="input" type="number" min={0} max={MAX_REMEMBER_DAYS} step={1} style={{ width: 90 }} value={p.rememberDays}
                onChange={(e) => setP({ ...p, rememberDays: Math.max(0, Math.min(MAX_REMEMBER_DAYS, Math.floor(Number(e.target.value) || 0))) })} data-testid="mfa-remember-days" />
              <span className="muted">day{p.rememberDays === 1 ? '' : 's'}{p.rememberDays === 0 ? ' (off)' : ''}</span>
            </div>
            <span className="faint" style={{ fontSize: 12 }}>
              At the code step people can tick “Don’t ask for a code on this device for {p.rememberDays || 'N'} days”. The password is always asked for, and the code
              again from another network (the same /24 for IPv4, /64 for IPv6), after a password change, or when two-step sign-in is reset. Administrators too.
              0 removes the choice and ignores devices already remembered. People see and remove theirs under My account.
            </span>
          </div>
        </>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn primary sm" disabled={!dirty || busy} onClick={() => void save()} data-testid="mfa-policy-save">{busy && <Loader2 size={13} className="spin" />} Save</button>
      </div>
    </section>
  );
}
